import { html, getAll, get } from '@bigyouth/base'
import setAttributes from '../utils/setAttributes'
import { create as createPipe } from '../utils/sigpipe'

const VALID_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
const VALID_TEL = /^(?:(?:\+|00)33[\s.-]{0,3}(?:\(0\)[\s.-]{0,3})?|0)[1-9](?:(?:[\s.-]?\d{2}){4}|\d{2}(?:[\s.-]?\d{3}){2})$/ // eslint-disable-line
const VALID_DATE = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/

export const FORM_ACTIONS = {
  SUBMIT: 'FORM_SUBMIT',
  ERROR: 'FORM_ERROR',
}

/**
 * FormValidation makes sure that specified inputs are correctly filled before submitting the form.
 * By default, FormValidation trigger the native action, if a form ID is specified via its props,
 * the native action is overrided and an event is triggered with the corresponding id.
 *
 * data-validation-type="[FIELD_TYPE]" should be added on the js-form-validation-field for specific
 * tests. It can be the following: email; tel; date; checkbox; radio; length.
 * Note: for radios, add the attributes on the first one.
 *
 * !! This feature is a template and can be modified to suit the project needs.
 *
 * Props:
 * isCustomSubmit(boolean, optional): if the component should inject an id and trigger an event on submit
 */
export default {
  name: 'form-validation',

  hasError: false,

  ui: {
    'fieldWrappers[]': '[js-form-validation-field-wrapper]',
    'fields[]': '[js-form-validation-field]',
    submit: '[js-form-validation-submit]',
  },

  events: [
    {
      on: 'submit',
      handle (e) {
        if (!this.ui.submit) {
          this.handleSubmit(e)
        }
      },
    },
  ],

  componentDidMount () {
    this.el.setAttribute('novalidate', '')

    if (this.props && (this.props.isCustomSubmit || this.props.isCustomSubmit2)) {
      this.pipe = createPipe()
      setAttributes({ dataFormId: `${this.pipe.id}` })(this.el)
    }

    getAll('[js-form-validation-field]', this.el).forEach(field => {
      // on blur
      field.addEventListener('blur', (e) => {
        const field = e.target
        const wrapper = field.closest('[js-form-validation-field-wrapper]') || field.parentNode
        this.removeError(field)

        if (field.required) {
          if (field.value !== '') {
            this.checkField(field, wrapper)
          } else {
            this.hasError = true
            wrapper.classList.add('has-error')
            wrapper.appendChild(
              this.createErrorMessage(config.trads['form.errors.generic']),
            )
          }
        } else {
          if (field.value !== '' && field.type !== 'radio') {
            this.checkField(field, wrapper)
          }
        }
      })

      // on change
      field.addEventListener('change', (e) => {
        this.removeError(e.target)
      })

      // on input
      field.addEventListener('input', (e) => {
        this.removeError(e.target)
      })
    })

    if (this.ui.submit) {
      this.ui.submit.addEventListener('click', () => {
        this.handleSubmit()

        if (!this.hasError) {
          this.el.submit()
        }
      })
    }
  },

  handleSubmit (e) {
    // Reset errors
    this.hasError = false
    this.removeErrors()

    getAll('[js-form-validation-field]', this.el).forEach((field) => {
      const wrapper =
        field.closest('[js-form-validation-field-wrapper]') || field.parentNode

      this.removeError(field)

      if (field.required) {
        if (field.value !== '') {
          this.checkField(field, wrapper)
        } else {
          this.hasError = true
          wrapper.classList.add('has-error')
          wrapper.appendChild(
            this.createErrorMessage(config.trads['form.errors.generic']),
          )
        }
      } else if (
        (field.type === 'radio' && field.checked) ||
        (field.type !== 'radio' && field.value !== '')
      ) {
        this.checkField(field, wrapper)
      } else {
        if (field.value !== '' && field.type !== 'radio') {
          this.checkField(field, wrapper)
        }
      }
    })

    const formData = this.ui.fields.reduce((acc, field) => {
      if (field.type === 'radio') {
        const selectedRadio = get(
          `input[name="${field.name}"]:checked`,
          this.el,
        )
        acc[field.name] = selectedRadio ? selectedRadio.value : ''
      } else if (field.type === 'checkbox') {
        acc[field.name] = field.checked ? 1 : 0
      } else {
        acc[field.name] = field.value
      }
      return acc
    }, {})

    if (this.hasError) {
      if (e) {
        e.preventDefault()
      }

      if (this.props && this.props.isCustomSubmit) {
        this.pipe.write({ action: FORM_ACTIONS.ERROR, formData })
      }
    } else if (this.props && this.props.isCustomSubmit) {
      if (e) {
        e.preventDefault()
      }

      this.pipe.write({ action: FORM_ACTIONS.SUBMIT, formData })
    } else if (this.props && this.props.isCustomSubmit2) {
      this.pipe.write({ action: FORM_ACTIONS.SUBMIT, formData })
    }
  },

  checkField (field, wrapper) {
    const validationtype = field.getAttribute('data-validation-type')
    let isValid = true
    let errorLabel = 'Champ incorrect'

    switch (validationtype) {
      case 'email':
        isValid = VALID_EMAIL.test(field.value)
        if (!isValid) errorLabel = config.trads['form.errors.mail']
        break

      case 'siret':
        isValid = field.value.length === 14
        if (!isValid) errorLabel = config.trads['form.errors.siret']
        break

      case 'password':
        /* eslint-disable-next-line */
        isValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d\/@$!%*#?&]{9,4096}$/.test(field.value)
        if (!isValid) errorLabel = config.trads['form.errors.password']
        break

      case 'birthdate':
        /* eslint-disable-next-line */
        const birthFields = getAll('[data-validation-type="birthdate"]')
        /* eslint-disable-next-line */
        const birthFieldsFilled = birthFields.filter(select => select.value !== '')

        // if date complete
        if (birthFieldsFilled.length === 3) {
          let date = ''

          birthFields.forEach((field, index) => {
            date += field.value

            if (index < 2) {
              date += '/'
            }
          })

          if (this.getAge(date) < 18) {
            isValid = false
            if (!isValid) errorLabel = config.trads['form.errors.age']
          }
        }

        break

      case 'passwordConfirm': {
        const passwordValue = get('[js-form-validation-password]').value
        if (passwordValue !== field.value) {
          isValid = false
        }

        if (!isValid) errorLabel = config.trads['form.errors.samePassword']
        break
      }

      case 'tel':
        isValid = VALID_TEL.test(field.value)
        if (!isValid) errorLabel = config.trads['form.errors.phone']
        break

      case 'date':
        isValid = VALID_DATE.test(field.value)
        if (!isValid) errorLabel = config.trads['form.errors.date']
        break

      case 'radio': {
        const radios = getAll(`input[name="${field.name}"]:checked`, wrapper)
        isValid = radios.length > 0
        if (!isValid) errorLabel = config.trads['form.errors.choice']
        break
      }

      case 'checkbox':
        isValid = field.checked
        if (!isValid) errorLabel = config.trads['form.errors.check']
        break

      case 'zipcode':
        isValid = field.value.length === 5
        if (!isValid) errorLabel = config.trads['form.errors.zipcode']
        break

      case 'length':
        isValid = this.checkFieldLength(field)
        if (!isValid) errorLabel = 'Longueur incorrecte'
        break
    }

    if (!isValid) {
      wrapper.classList.add('has-error')
      wrapper.appendChild(this.createErrorMessage(errorLabel))
    }

    this.hasError = this.hasError || !isValid
  },

  checkFieldLength (field) {
    const minChar = parseInt(field.getAttribute('minlength')) || 0
    const maxChar = parseInt(field.getAttribute('maxlength')) || Infinity

    return field.value.length <= maxChar && field.value.length >= minChar
  },

  createErrorMessage (content) {
    const node = document.createElement('div')
    node.innerHTML = html`
      <div class="form__field-error" js-form-validation-error>${content}</div>
    `

    return node.firstElementChild
  },

  removeErrors () {
    this.ui.fields.forEach(this.removeError)
  },

  removeError (field) {
    const wrapper =
      field.closest('[js-form-validation-field-wrapper]') || field.parentNode

    if (wrapper.classList.contains('has-error')) {
      const errorLabel = get('[js-form-validation-error]', wrapper)

      wrapper.classList.remove('has-error')

      if (errorLabel) errorLabel.remove()
    }
  },

  getAge (date) {
    const today = new Date()
    const birthDate = new Date(date)
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    return age
  },
}
